import { useMemo, useState } from 'react';
import { useAsyncProductSearch } from './useAsyncProductSearch';

export interface ProductItem {
  id: number;
  productName: string;
  productCode: string;
  quantity: string;
  productId?: number;
  originalItemId?: number;
  /** Catalog "código proveedor" (workshop_code), if the product has one. */
  workshopCode?: string;
}

export interface Product {
  id: number;
  name: string;
  code: string;
  /** Catalog "código proveedor" (workshop_code). */
  workshopCode?: string;
}

export interface UseProductFormProps {
  item: ProductItem;
  index: number;
  products: Product[]; // Keep for backward compatibility, but will use async search
  canRemove: boolean;
  onRemove: (id: number) => void;
  onUpdate: (id: number, field: keyof ProductItem, value: string) => void;
  selectedProductIds?: number[];
  useAsyncSearch?: boolean; // New prop to enable async search
}

export const useProductForm = ({
  item,
  index,
  products,
  canRemove,
  onRemove,
  onUpdate,
  selectedProductIds = [],
  useAsyncSearch = true, // Default to true for new async search
}: UseProductFormProps) => {
  const [searchInput, setSearchInput] = useState('');

  // Use async search if enabled
  const {
    products: asyncProducts,
    loading: asyncLoading,
    hasMore: asyncHasMore,
    loadMore: asyncLoadMore,
  } = useAsyncProductSearch({
    searchTerm: searchInput,
    selectedProductIds,
    currentProductId: item.productId,
    enabled: useAsyncSearch,
  });

  // Filtrar productos disponibles - excluir los ya seleccionados, pero incluir el producto actual del item
  const availableProducts = useMemo(() => {
    if (useAsyncSearch) {
      return asyncProducts;
    }
    // Fallback to old behavior
    return products.filter(
      (product) => !selectedProductIds.includes(product.id) || product.id === item.productId,
    );
  }, [useAsyncSearch, asyncProducts, products, selectedProductIds, item.productId]);

  // Manejar selección de producto
  const handleProductSelect = (product: Product | null) => {
    if (product) {
      onUpdate(item.id, 'productName', product.name);
      onUpdate(item.id, 'productCode', product.code);
      onUpdate(item.id, 'productId', product.id.toString());
      onUpdate(item.id, 'workshopCode', product.workshopCode?.trim() ?? '');
      return;
    }

    // Clear current selection when user starts a new search.
    onUpdate(item.id, 'productName', '');
    onUpdate(item.id, 'productCode', '');
    onUpdate(item.id, 'productId', '');
    onUpdate(item.id, 'workshopCode', '');
  };

  // Manejar cambio en cantidad
  const handleQuantityChange = (value: string) => {
    onUpdate(item.id, 'quantity', value);
  };

  // Manejar eliminación del item
  const handleRemove = () => {
    onRemove(item.id);
  };

  // Determinar si el formulario es válido
  const hasSelectedProduct = useMemo(() => {
    return !!(item.productName && item.productCode);
  }, [item.productName, item.productCode]);

  const isValid = useMemo(() => {
    if (!hasSelectedProduct) {
      return false;
    }
    const qty = Number(item.quantity);
    return item.quantity !== '' && !Number.isNaN(qty) && qty > 0;
  }, [hasSelectedProduct, item.quantity]);

  const showQuantityError = useMemo(() => {
    if (!hasSelectedProduct || item.quantity === '') {
      return false;
    }
    const qty = Number(item.quantity);
    return Number.isNaN(qty) || qty <= 0;
  }, [hasSelectedProduct, item.quantity]);

  // Determinar si hay productos disponibles
  const hasAvailableProducts = useMemo(() => {
    return availableProducts.length > 0;
  }, [availableProducts]);

  // Obtener el producto seleccionado actual
  // Prefer product from availableProducts for reference equality with Autocomplete
  const selectedProduct = useMemo(() => {
    if (!item.productId) return null;

    // First try to find in availableProducts (for reference equality)
    const foundInAvailable = availableProducts.find((product) => product.id === item.productId);
    if (foundInAvailable) {
      const workshopCode =
        foundInAvailable.workshopCode?.trim() || item.workshopCode?.trim() || undefined;
      if (workshopCode === foundInAvailable.workshopCode) {
        return foundInAvailable;
      }
      return { ...foundInAvailable, workshopCode };
    }

    // Fallback: create product object from item data if we have name/code
    if (item.productName && item.productCode) {
      return {
        id: item.productId,
        name: item.productName,
        code: item.productCode,
        workshopCode: item.workshopCode?.trim() ? item.workshopCode : undefined,
      };
    }

    return null;
  }, [item.productId, item.productName, item.productCode, item.workshopCode, availableProducts]);

  // Determinar si el producto actual está disponible
  const isCurrentProductAvailable = useMemo(() => {
    return selectedProduct !== null;
  }, [selectedProduct]);

  return {
    availableProducts,
    handleProductSelect,
    handleQuantityChange,
    handleRemove,
    isValid,
    showQuantityError,
    hasAvailableProducts,
    selectedProduct,
    isCurrentProductAvailable,
    item,
    index,
    canRemove,
    // Async search props
    searchInput,
    setSearchInput,
    asyncLoading,
    asyncHasMore,
    asyncLoadMore,
    useAsyncSearch,
  };
};
